import { Link } from "react-router-dom";
import { Heart, Mail, Sparkles } from "lucide-react";

const FOUNDER_IMG = "https://customer-assets.emergentagent.com/job_shared-stories-31/artifacts/xkmobnou_IMG_4798.jpg";

export default function Founder() {
  return (
    <div data-testid="page-founder">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-7">
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-6">
            <span className="editorial-rule" />Meet the founder
          </div>
          <h1 className="font-serif text-5xl lg:text-7xl text-mindora-ink leading-[1.05] tracking-tight">
            Hi, I'm <em className="italic text-mindora-red">Jiya.</em>
          </h1>
          <p className="mt-8 text-lg leading-relaxed text-mindora-muted max-w-xl">
            I'm 17, I read too many books, have strong opinions about playlists, and I think
            feeling things deeply is a superpower — even when it doesn't feel like one.
          </p>
          <p className="mt-5 text-lg leading-relaxed text-mindora-muted max-w-xl">
            I started Mindora because I kept noticing something: everyone around me was
            carrying something heavy, but doing it quietly. Like we'd all silently agreed to
            act fine. I wanted to break that agreement. <em className="text-mindora-ink">Not loudly. Just honestly.</em>
          </p>
          <p className="mt-5 text-lg leading-relaxed text-mindora-muted max-w-xl">
            This platform is my attempt at building the space I wished existed — somewhere you can
            say <em className="text-mindora-ink">"this is hard"</em> and find out that someone else
            already typed those exact words at 2am.
          </p>
        </div>

        <div className="lg:col-span-5">
          <div className="relative rounded-3xl overflow-hidden border border-mindora-line shadow-clay">
            <img
              src={FOUNDER_IMG}
              alt="Jiya, founder of Mindora"
              data-testid="founder-photo"
              className="w-full h-[560px] object-cover"
            />
          </div>
          <div className="mt-5 px-1">
            <div className="font-serif text-2xl text-mindora-ink">Jiya</div>
            <div className="text-xs uppercase tracking-[0.2em] text-mindora-red mt-1">Founder · 17 · still figuring it out</div>
            <p className="text-sm text-mindora-muted mt-3 leading-relaxed">
              Reader, playlist-purist, builder of quiet things. Believer that the most useful
              sentence in the world is sometimes just: <em className="text-mindora-ink">I get it.</em>
            </p>
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="bg-mindora-panel border-y border-mindora-line mt-24 py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-6 text-center">
            <span className="editorial-rule" />A small manifesto
          </div>
          <div className="space-y-10 text-mindora-ink font-serif text-2xl lg:text-3xl leading-[1.4]">
            <p>
              We believe in the slow medicine of <em className="text-mindora-red">being known</em>.
              Not viral. Not optimized. Just known.
            </p>
            <p>
              We believe anonymity is not the opposite of honesty — sometimes it's the gate to it.
            </p>
            <p>
              We believe a story with a thousand <em className="text-mindora-red">"I Relate"</em>s
              is not a post. It's a quiet proof: <em>you've never been the only one.</em>
            </p>
            <p>
              We believe technology should make rooms warmer, not louder.
            </p>
            <p>
              And we believe that mental health is not a destination — it's a small daily practice,
              built by gratitudes you almost didn't write down, letters to a future self you might one day
              need to read, and conversations that start with <em className="text-mindora-red">I get it.</em>
            </p>
          </div>
        </div>
      </section>

      {/* What we hold sacred */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-3 gap-7">
        <Pillar
          icon={<Heart size={18} />}
          title="Safety first, always"
          body="Stories carrying signs of crisis surface to our clinical team. We list real, vetted psychologists you can reach directly — never AI advice in sensitive moments."
        />
        <Pillar
          icon={<Sparkles size={18} />}
          title="Anonymity is a feature, not a flaw"
          body="You decide every time you publish. Your name never appears on anonymous posts — not in our database, not in our analytics."
        />
        <Pillar
          icon={<Mail size={18} />}
          title="No ads. Ever."
          body="Mindora will never sell your sentences to anyone. Counselor sessions and gentle memberships keep the lights on."
        />
      </section>

      {/* Contact / CTA */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-32 text-center">
        <h2 className="font-serif text-4xl sm:text-5xl text-mindora-ink leading-tight">
          Write to me directly.
        </h2>
        <p className="mt-5 text-mindora-muted text-lg leading-relaxed">
          Feedback, hard questions, partnership ideas, or a story you almost didn't send anywhere —
          I read every email myself.
        </p>
        <a
          href="mailto:jiyaatmindora@gmail.com"
          data-testid="founder-email-link"
          className="btn-primary inline-block mt-8 rounded-full px-9 py-4 text-base font-medium tracking-wide"
        >
          jiyaatmindora@gmail.com
        </a>
        <div className="mt-12 text-sm text-mindora-muted">
          Or just start —{" "}
          <Link to="/register" className="text-mindora-red hover:underline" data-testid="founder-cta-register">
            create your space
          </Link>.
        </div>
      </section>
    </div>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="rounded-3xl border border-mindora-line bg-mindora-bg p-8 shadow-clay hover:shadow-clayHover hover:-translate-y-1 transition-all duration-500">
      <span className="flex items-center justify-center w-10 h-10 rounded-full bg-mindora-red/10 text-mindora-red">
        {icon}
      </span>
      <h3 className="font-serif text-2xl text-mindora-ink leading-snug mt-5">{title}</h3>
      <p className="mt-3 text-sm text-mindora-muted leading-relaxed">{body}</p>
    </div>
  );
}
